<script setup lang="ts">
/**
 * mail-admin-handoff-v2-changes/CHANGES.md §2 -> "Find tenant palette", find-tenant-dialog.html.
 * Built on Dialog.vue's new chromeless + `placement="top"` support (no `title` prop -> the default
 * slot owns the whole panel: search row, results, footer). Mounted once in AppShell, bound to
 * `session.findTenantOpen` — same pattern as CreateTenantDialog/session.createTenantOpen.
 *
 * Filters `session.tenants` on the client (already fully loaded for the Tenants page and rail
 * counts — IMPLEMENTATION_PLAN.md §2 "Tenant search (palette)" — so there's no search endpoint).
 * An ARIA combobox: the input is `role="combobox"`, results are a `listbox`/`option` list, and the
 * highlighted option is tracked via `aria-activedescendant` rather than moving DOM focus — focus
 * stays on the input the whole time the palette is open.
 */
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { createTenantOpen, findTenantOpen, tenants } from '@/session';
import { recentTenants } from '@/recentTenants';
import type { Tenant } from '@/types';
import Dialog from '@/components/ui/Dialog.vue';
import Tile from '@/components/ui/Tile.vue';
import { Plus, Search } from '@lucide/vue';

const router = useRouter();

const query = ref('');
const highlightedIndex = ref(0);

const trimmedQuery = computed(() => query.value.trim());

const filteredTenants = computed<Tenant[]>(() => {
  const q = trimmedQuery.value.toLowerCase();
  if (!q) return [];
  return tenants.value.filter(
    tenant => tenant.name.toLowerCase().includes(q) || tenant.slug.toLowerCase().includes(q) || tenant.domain.toLowerCase().includes(q)
  );
});

const showNoMatch = computed(() => trimmedQuery.value !== '' && filteredTenants.value.length === 0);

/** Recent (up to 5) with an empty query, otherwise the first 8 matches. */
const visibleResults = computed<Tenant[]>(() => (trimmedQuery.value ? filteredTenants.value.slice(0, 8) : recentTenants.value));

const groupLabel = computed(() => {
  if (!trimmedQuery.value) return 'Recent';
  const n = filteredTenants.value.length;
  return n === 1 ? '1 match' : `${n} matches`;
});

const hint = computed(() => {
  if (showNoMatch.value) return '';
  if (!trimmedQuery.value) return `Type to search all ${tenants.value.length} tenants by name, slug or domain.`;
  if (filteredTenants.value.length > 8) return `Showing 8 of ${filteredTenants.value.length}. Keep typing to narrow it down.`;
  return '';
});

function optionId(index: number): string {
  return `find-tenant-option-${index}`;
}

const highlightedOptionId = computed(() =>
  !showNoMatch.value && visibleResults.value.length > 0 ? optionId(highlightedIndex.value) : undefined
);

// Reset to a clean slate every time the palette closes, so reopening it never shows a stale query
// or a highlight left over from the last search (mirrors CreateTenantDialog's reset-on-close).
watch(findTenantOpen, isOpen => {
  if (!isOpen) {
    query.value = '';
    highlightedIndex.value = 0;
  }
});

// The result set changes shape on every keystroke — keep the highlight in range and predictable
// (always the top row of whatever's currently showing) instead of pointing at a row that moved.
watch(visibleResults, () => {
  highlightedIndex.value = 0;
});

function moveHighlight(delta: number) {
  const count = visibleResults.value.length;
  if (count === 0) return;
  highlightedIndex.value = (highlightedIndex.value + delta + count) % count;
}

function selectTenant(tenant: Tenant) {
  findTenantOpen.value = false;
  router.push({ name: 'tenant-keys', params: { slug: tenant.slug } });
}

function onInputKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveHighlight(1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveHighlight(-1);
  } else if (event.key === 'Enter') {
    event.preventDefault();
    const tenant = visibleResults.value[highlightedIndex.value];
    if (tenant) selectTenant(tenant);
  }
}

function close() {
  findTenantOpen.value = false;
}

/** Both the footer "+ New tenant" and the no-match "Create a tenant instead" link do this. */
function openCreateTenant() {
  findTenantOpen.value = false;
  createTenantOpen.value = true;
}
</script>

<template>
  <Dialog :open="findTenantOpen" placement="top" width="560px" aria-label="Find tenant" @close="close">
    <div class="flex h-[60px] shrink-0 items-center gap-3 border-b border-line-soft pr-4 pl-5">
      <Search :size="18" :stroke-width="1.9" class="shrink-0 text-ink-3" aria-hidden="true" />
      <input
        v-model="query"
        autofocus
        type="search"
        role="combobox"
        aria-label="Find a tenant"
        aria-expanded="true"
        aria-controls="find-tenant-results"
        :aria-activedescendant="highlightedOptionId"
        placeholder="Find a tenant by name, slug or domain"
        class="h-10 min-w-0 grow border-0 bg-transparent text-base text-ink placeholder:text-icon-muted focus:outline-none focus-visible:shadow-none"
        @keydown="onInputKeydown"
      />
      <button
        type="button"
        aria-label="Close"
        class="flex h-6 shrink-0 items-center rounded-tag border border-line bg-sunken px-[7px] font-mono text-[11px] text-ink-3"
        @click="close"
      >
        Esc
      </button>
    </div>

    <div class="flex min-h-0 flex-col overflow-y-auto p-2">
      <div v-if="showNoMatch" class="flex flex-col items-center gap-2 px-3 py-10 text-center">
        <p class="text-sm font-semibold text-ink">No tenant matches &quot;{{ trimmedQuery }}&quot;</p>
        <button
          type="button"
          class="type-control text-ink underline decoration-line-strong underline-offset-3 hover:decoration-ink"
          @click="openCreateTenant"
        >
          Create a tenant instead
        </button>
      </div>

      <template v-else>
        <div class="flex items-center justify-between px-3 pt-2.5 pb-1.5">
          <span class="type-overline text-ink-3">{{ groupLabel }}</span>
        </div>

        <ul id="find-tenant-results" role="listbox" aria-label="Tenants" class="flex flex-col gap-0.5">
          <li v-for="(tenant, index) in visibleResults" :key="tenant.slug">
            <div
              :id="optionId(index)"
              role="option"
              :aria-selected="index === highlightedIndex"
              class="grid min-h-[52px] cursor-pointer grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 rounded-control px-3 py-2"
              :class="index === highlightedIndex ? 'bg-signal-tint shadow-[inset_0_0_0_1px_var(--color-signal-ring)]' : ''"
              @mouseenter="highlightedIndex = index"
              @click="selectTenant(tenant)"
            >
              <Tile :name="tenant.name" :size="32" :tone="index === highlightedIndex ? 'ink' : 'tile'" />

              <span class="flex min-w-0 flex-col gap-px">
                <span class="truncate text-sm font-semibold text-ink">{{ tenant.name }}</span>
                <span class="truncate font-mono text-xs text-ink-3">{{ tenant.slug }}</span>
              </span>

              <span class="flex items-center gap-2.5">
                <span class="text-[13px] text-ink-3">{{ tenant.domain }}</span>
                <span
                  v-if="index === highlightedIndex"
                  aria-hidden="true"
                  class="flex h-[22px] items-center rounded-[5px] border border-signal-line bg-surface px-1.5 font-mono text-[11px] text-signal-ink"
                >
                  ↵
                </span>
              </span>
            </div>
          </li>
        </ul>

        <p v-if="hint" class="px-3 pt-2.5 pb-1.5 text-[13px] leading-[19px] text-ink-3">{{ hint }}</p>
      </template>
    </div>

    <div class="flex h-11 shrink-0 items-center justify-between gap-4 border-t border-line-soft bg-sunken pr-3 pl-4">
      <div aria-hidden="true" class="flex items-center gap-3.5 text-xs text-ink-3">
        <span class="flex items-center gap-1.5">
          <span class="flex h-5 min-w-5 items-center justify-center rounded-tag border border-line bg-surface font-mono text-[11px]">↑</span>
          <span class="flex h-5 min-w-5 items-center justify-center rounded-tag border border-line bg-surface font-mono text-[11px]">↓</span>
          Move
        </span>
        <span class="flex items-center gap-1.5">
          <span class="flex h-5 min-w-5 items-center justify-center rounded-tag border border-line bg-surface font-mono text-[11px]">↵</span>
          Open
        </span>
        <span class="flex items-center gap-1.5">
          <span class="flex h-5 items-center rounded-tag border border-line bg-surface px-1.5 font-mono text-[11px]">Esc</span>
          Close
        </span>
      </div>

      <button type="button" class="flex h-8 items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-ink" @click="openCreateTenant">
        <Plus :size="14" :stroke-width="2" aria-hidden="true" />
        New tenant
      </button>
    </div>
  </Dialog>
</template>
